import {Grid, Stack} from '@mantine/core';
import type {FC, ReactNode} from 'react';

/**
 * Responsive two-column detail-page layout: `DetailGrid.Main` (8-col) +
 * `DetailGrid.Sidebar` (4-col), stacking full-width on mobile.
 * @registry Two-column detail-page layout (main content + sidebar).
 *
 * Ported from `library-m/layout/DetailGrid`. Prop surface and compound
 * exports (`DetailGrid.Main`, `DetailGrid.Sidebar`, plus the standalone
 * `DetailGridMain`/`DetailGridSidebar` exports) kept identical.
 *
 * Usage:
 * ```tsx
 * <DetailGrid>
 *   <DetailGrid.Main>
 *     <Card>Main content here</Card>
 *   </DetailGrid.Main>
 *   <DetailGrid.Sidebar>
 *     <Card>Sidebar content</Card>
 *   </DetailGrid.Sidebar>
 * </DetailGrid>
 * ```
 */

export interface DetailGridProps {
  children: ReactNode;
  /** Gap between grid items. Default: "lg" */
  gutter?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface DetailGridMainProps {
  children: ReactNode;
  /** Gap between stacked items. Default: "md" */
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface DetailGridSidebarProps {
  children: ReactNode;
  /** Gap between stacked items. Default: "md" */
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const DetailGridMain: FC<DetailGridMainProps> = ({children, gap = 'md'}) => (
  <Grid.Col span={{base: 12, lg: 8}}>
    <Stack gap={gap}>{children}</Stack>
  </Grid.Col>
);

const DetailGridSidebar: FC<DetailGridSidebarProps> = ({children, gap = 'md'}) => (
  <Grid.Col span={{base: 12, lg: 4}}>
    <Stack gap={gap}>{children}</Stack>
  </Grid.Col>
);

const DetailGridRoot: FC<DetailGridProps> = ({children, gutter = 'lg'}) => {
  return <Grid gutter={gutter}>{children}</Grid>;
};

export const DetailGrid = Object.assign(DetailGridRoot, {
  Main: DetailGridMain,
  Sidebar: DetailGridSidebar,
});

export {DetailGridMain, DetailGridSidebar};
